import { FC, useEffect, useRef, useState } from 'react';
import { ITextFieldProps, TextField } from './TextField';

export type IIntegerFieldProps = Omit<ITextFieldProps, 'type' | 'inputmode' | 'value' | 'onChange' | 'onInput'> & {
    max?: number;
    value?: number;
    onInput?: (value: number | undefined) => void;
    onChange?: (value: number | undefined) => void;
};

/**
 * Field only allowing integer values to be entered.
 * Technically, it only allows positive integers;
 * we can add support for negatives later if needed.
 */
export const IntegerField: FC<IIntegerFieldProps> = ({
    max = 9999999999,
    value,
    onInput,
    onChange,
    ...otherProps
}) => {

    const fieldRef = useRef<HTMLIonInputElement>(null);

    // maintain internal copy of value, so we can revert invalid input
    const [internalValue, setInternalValue] = useState<number | undefined>(value);
    useEffect(() => setInternalValue(value), [value]);

    const isValid = (input: string) => {
        // empty string is allowed
        if (input === '') {
            return true;
        }
        // otherwise, must be digits only
        if (!input?.match(/^[0-9]+$/)) {
            return false;
        }
        // and must be in range
        if (Number(input) < 0 || Number(input) > max) {
            return false;
        }
        // and must not have multiple leading zeros
        if (input.startsWith('00')) {
            return false;
        }
        return true;
    };

    const toInteger = (input: string) => {
        if (!isValid(input)) {
            throw new Error(`cannot convert ${input} to integer`);
        }
        return (!input ? undefined : Number(input));
    };

    const toString = (integer: number | undefined) => {
        return (integer === undefined ? '' : String(integer));
    };

    const handleInput = (input: string) => {
        if (!isValid(input)) {
            const oldValue = toString(internalValue);
            fieldRef.current!.value = oldValue;
            return;
        }

        const newValue = toInteger(input);
        setInternalValue(newValue);

        if (onInput) {
            onInput(newValue);
        }
    };

    const handleChange = () => {
        if (onChange) {
            onChange(internalValue);
        }
    };

    const handleFocus = async () => {
        // select all when field gets focus
        const input = await fieldRef.current?.getInputElement();
        if (input) {
            // this combination of calls with a timeout was found by trial and error to be
            // the only way to highlight the input text without causing a popup menu to open
            input.setSelectionRange(0, 0);
            setTimeout(() => input.select());
        }
    };

    return (
        <TextField
            {...otherProps}
            inputRef={fieldRef}
            inputmode="numeric"
            value={toString(internalValue)}
            onFocus={handleFocus}
            onInput={handleInput}
            onChange={handleChange}
        />
    );
};
